// @ts-strict-ignore
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { Location } from '@angular/common';
import { SohoBusyIndicatorDirective, SohoStepProcessComponent, SohoToastService, } from 'ids-enterprise-ng';
import { LMFormPanelBase } from './lm-form-panel-base';
import { AppConstants } from '@utils/app.constants';
import { LMPanelTransactionArgs } from '@form/services/lm-form-action-util.service';
import { LMFormTransactionService } from '@form/services/lm-form-transaction.service';
import { LMActionService } from '@services/lm-action.service';
import { LMModel, LMModelStatusEvent } from '@form/model/lm-model';
import {
  LMFormPanelBaseService,
  LMGetPanelTransactionOptions,
  LMPanelTransactionEvent,
  PanelTransactionEnum
} from '@form/services/lm-form-panel.service';
import { LandmarkPanel, LMFormContentBase } from './lm-form.model';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { LMFormState } from '@states/models/lm-form-state.model';
import { LMFormPersonalization } from '@states/models/lm-form-personalization.model';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMTransactionState } from '@transaction/lm-transaction-state';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMErrorUtilService } from '@services/utils/lm-error-util.service';
import { LMFormPagingService } from '@form/services/lm-form-paging.service';
import { LMModelServiceAdapter } from '@form/model/lm-model-service-adapter';
import { LMWorker } from '../../types/lm-worker';
import { LMFormRequestService, LMStepPanelEvent, PanelTransaction } from './services';
import { LMFormPanelFormComponent } from './lm-form-panel-form.component';
import { LMFormPanelUrlComponent } from './lm-form-panel-url.component';
import { LMModelChangedEvent } from './model';
import { Observable, Observer, SubscriptionLike as ISubscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { LMFormatUtilService } from '@services/utils';
import { LMConditionUtilService, LMContextUtilService, LMGlobalUtilService } from '@utils/index';
import * as urltemplate from 'url-template';
import { LMFormPanelMultiListComponent } from './lm-form-panel-multilist.component';
import { LMNavigationService } from '@navigation/lm-navigation.service';
import { LMContextService, LMUrlInfoBuilderService } from '@services/index';
import { LMFormPanelListComponent } from './lm-form-panel-list.component';
import { LMInvalidFieldEvent, LMRefreshRequest } from '@app/components';

export class LMStepPanelSaveWorker extends LMWorker {
  private formPanels: LMFormPanelBase[];

  constructor(formPanels: LMFormPanelBase[]) {
    super();

    this.formPanels = formPanels;
  }

  protected has(i: number): boolean {
    return (i < this.formPanels.length);
  }

  protected get(i: number): LMFormPanelBase {
    return this.formPanels[ i ];
  }

  // this shouldn't save the form panels, form is going to do that
  protected run(i: number): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const formPanel = this.formPanels[ i ];
      if (formPanel && formPanel.isDirty()) {
        formPanel.save(observer, false);
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }
}

@Component({
  selector:    'lm-form-content-stepprocess',
  standalone: false,
  templateUrl: 'lm-form-content-stepprocess.component.html',
  styleUrls:   [ 'lm-form-content-stepprocess.component.scss' ],
  providers:   [ LMFormPanelBaseService ],
})

export class LMFormContentStepprocessComponent implements AfterViewInit, AfterViewChecked, OnInit, OnDestroy, LMFormContentBase {
  @Input() model: LMModel;
  @Input() specFields;
  @Input() formUrlInfo: LMFormUrlInfo;
  @Input() formSpec: LMFormSpec = null;
  @Input() placeHolder: ViewContainerRef;
  @Input() formState: LMFormState;
  @Input() formPersonalization: LMFormPersonalization;

  get fromDataView(): LMDataView {
    return this.m_fromDataView;
  }

  @Input() set fromDataView(fromDataView: LMDataView) {
    this.m_fromDataView = fromDataView;
  }

  @Output() contentModified = new EventEmitter<any>();
  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();
  @Output() stepPanelEvent: EventEmitter<LMStepPanelEvent> = new EventEmitter<LMStepPanelEvent>();
  @Output() specialActionToolbarButtonEvent = new EventEmitter<LMSpecialActionToolbarButtonEvent>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() panelTransactionEvent = new EventEmitter<LMPanelTransactionEvent>();
  @Output() drillAroundEvent = new EventEmitter<LMDrillAroundEvent>();

  /*
   * Using a template variable because using the detail section base class doesn't
   * seem to return my children.
   * @ViewChildren(LMDetailSectionBase) detailListComponents: QueryList<LMDetailSectionBase>;
   */
  @ViewChildren('panelComponent') panelComponents: QueryList<LMFormPanelBase>; // stan should this be stepComponent
  @ViewChild('sohoStepProcessComponent') sohoStepProcessComponent: SohoStepProcessComponent;
  @ViewChild(SohoBusyIndicatorDirective) sohoBusyIndicator: SohoBusyIndicatorDirective;
  @ViewChild('dialogPlaceholder', { read: ViewContainerRef, static: true }) placeholder: ViewContainerRef;

  @HostBinding('attr.data-automation-id') public automationId: string;

  public stepPanelTabIdSuffix = AppConstants.ELEMENT_ID_SUFFIX_STEP_PANEL_TAB;

  public panels: Array<LandmarkPanel>;
  public nextButtonText = Soho.Locale.translate('Next');
  public isNextEnabled: boolean;
  public isPreviousEnabled: boolean;
  public enforceLinearWhen = false;
  public firstDirtyPanel = -1;
  public initialStepIgnore = true; // temp fix for initial fire of the beforeSelectStep.
  private selectedPanelId: string;
  private m_fromDataView: LMDataView;
  private modelChangedSubscription: ISubscription;
  private modelStatusChangesSubscription: ISubscription;
  private gotoPanelId: string;
  private formReloading = false;
  private isDataModNotAllowed: boolean;
  public testVersion1: boolean;
  public uiLoadingIndicatorTimeout = 0;

  public hasBreadcrumbs = false;

  // uncomment once IDS allows attributes for the step process control
  // public stepProcessAttributes: SohoAttributesArray = [];

  constructor(
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmLoggerService: LMLoggerService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmNavigationService: LMNavigationService,
    private changeDetectionRef: ChangeDetectorRef,
    private location: Location,
    private lmConditionUtilService: LMConditionUtilService,
    private lmModelServiceAdapter: LMModelServiceAdapter,
    private lmActionService: LMActionService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmFormPanelBaseService: LMFormPanelBaseService,
    private lmFormTransactionService: LMFormTransactionService,
    private lmFormRequestService: LMFormRequestService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmSohoToastService: SohoToastService,
    private lmFormPagingService: LMFormPagingService,
    private ngZone: NgZone,
    private lmContextUtilService: LMContextUtilService,
    private lmContextService: LMContextService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
  ) {
    this.uiLoadingIndicatorTimeout = this.lmContextService.uiLoadingIndicatorTimeout;
  }

  ngOnInit() {
    this.automationId = `lm-${this.formSpec.automationId}-form-content-stepprocess`;
    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;
    this.hasBreadcrumbs = this.lmContextService.isBreadCrumbsDisplayed();
    this.isDataModNotAllowed = this.lmContextService.isDataModificationNotAllowed();

    // uncomment once IDS allows attributes for the step process control
    // this.stepProcessAttributes.push({ name: 'data-automation-id', value: 'lm-' + this.formSpec.formElementId + '-tab' });

    if (this.formState.furthestPanelIndex >= 0) {
      this.lmFormPanelBaseService.furthestPanelIndex = this.formState.furthestPanelIndex;
    }

    if (this.formState.furthestSubPanelIndex >= 0) {
      this.lmFormPanelBaseService.furthestSubPanelIndex = this.formState.furthestSubPanelIndex;
    }

    this.lmFormPanelBaseService.buildCompositePanels(this.formSpec.busFormSpec, this.model, this.formSpec);
    this.panels = this.lmFormPanelBaseService.getPanels();

    this.selectedPanelId = this.lmFormPanelBaseService.getFurthestVisitedCompletedPanelId();
    if (this.formState.selectedPanelIndex >= 0) {
      this.selectedPanelId = this.lmFormPanelBaseService.getPanelIdByIndex(this.formState.selectedPanelIndex);
    }

    this.modelStatusChangesSubscription = this.model.statusChanges.subscribe((modelChangedEvent: LMModelStatusEvent) => {
      if (this.enforceLinearWhen) {
        this.updateFirstDirtyPanel();
      }
    });

    this.modelChangedSubscription = this.model.modelChanged.subscribe((modelChangedEvent: LMModelChangedEvent) => {
      // Could be getting called early. We hit next from the first step, execute the save transaction in form, set the
      // initialDataView on lm-form, which triggers the formLoaded method. This sets data on the model, which then
      // pings this subscription all before the panel transaction's callback has been called

      // only concerned when the form sets data (new dataView from the server), not interested in update where the form is updating the view
      // from initial value rules, compound field dialogs, etc.
      if (modelChangedEvent.eventType === 'set') {

        let pagingRequest = false;
        let hardRefresh = false;
        let originatingList: string = null;
        let isPanelTransaction = false;

        if (modelChangedEvent.source) {
          hardRefresh = modelChangedEvent.source.type === 'refresh';
          pagingRequest = modelChangedEvent.source.type === 'paging';

          if (modelChangedEvent.source instanceof LMPanelTransactionEvent) {
            originatingList = modelChangedEvent.source ? modelChangedEvent.source.originatingList : null;
            isPanelTransaction = modelChangedEvent.source && modelChangedEvent.source.type === 'transaction' ? true : false;
          }
        }

        // if panel transaction, then i get notified via a callback;
        if (isPanelTransaction) {
          this.markTabsForReload(hardRefresh, originatingList);
          return;
        }

        // need to only listen for a paging call.  If the form is saved using the next button
        // the step listens to an observer and refresh call runRefresh()
        if (pagingRequest) {
          this.refreshStepDataViews(this.getSelectedPanelSpec(), 'commit', false).pipe(first()).subscribe((finished: boolean) => {
            this.updateStepPanels(pagingRequest || hardRefresh);

            // LMCLIENT-20748
            if (!this.getSelectedPanelSpec() || !this.getSelectedPanelSpec().isValid ) {
              this.setSelectComponentId(this.panels[ 0 ].id);
              const selectedLandmarkPanel: LandmarkPanel = this.getSelectedPanelSpec();
              selectedLandmarkPanel.activated = true;
              this.gotoPanelId = this.panels[ 0 ].id;
              return;
            }

            this.updateNavigationControls();
            this.updateDataView(hardRefresh, originatingList);
          });
        }
      }
    });
  }

  ngAfterViewInit() {
    // DPB in a timeout to resolve a change detection error
    setTimeout(() => {
      this.processStepChange();
    }, 1);
  }

  ngAfterViewChecked() {
    if (this.gotoPanelId) {
      const panelId = this.gotoPanelId;
      this.gotoPanelId = undefined;
      setTimeout(() => this.goToPanel(panelId));
    }
  }

  ngOnDestroy() {
    this.lmLoggerService.log('onDestroy of step process');

    if (this.modelChangedSubscription) {
      this.modelChangedSubscription.unsubscribe();
    }

    if (this.modelStatusChangesSubscription) {
      this.modelStatusChangesSubscription.unsubscribe();
    }
  }

  onSaveClose(event: any) {
    this.runSaveAction().subscribe(
      (continueOn: boolean) => {
        if (continueOn) {
          if (this.lmContextService.inMingleOverlay()) {
            this.lmContextUtilService.sendOverlayCompleteMessage();
          } else {
            this.location.back();
          }
        } else {
          this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        }
      });
  }
  onBeforeSelectStep(request: BeforeSelectStepEvent) {
    this._onBeforeSelectStep(request);
  }
  _onBeforeSelectStep(request: BeforeSelectStepEvent) {
    // ignore the first call to the method as it will be the initial selection. in the future the
    // selection will be handled in the template and this will not be needed
    let targetPanel;

    if (this.initialStepIgnore) {
      this.initialStepIgnore = false;

      targetPanel = this.getPanelById(this.selectedPanelId);
      if (targetPanel === undefined) {
        // if targetPanel is undefined need to select the next or previous available panel
        this.selectedPanelId = this.lmFormPanelBaseService.getClosestPanel(this.selectedPanelId);
        if (this.selectedPanelId !== null) {
          setTimeout(() => this.goToPanel(this.selectedPanelId));
        }
        return;
      }

      const targetComponent = this.getSelectComponent();
      const loadSubPanels = targetComponent ? (!targetComponent.activated || targetComponent.needsReload) : (!targetPanel.activated || targetPanel.needsReload);

      if (targetPanel.hasSubPanels && loadSubPanels) {
        const panelName = targetPanel.busFormPanel.panelName;
        const loadSubPanelsUrl = this.lmUrlUtilService.buildSubPanelDataUrl(this.formUrlInfo, panelName);

        if (!this.lmContextService.useNewSpecUrls()) {
          const name = this.formUrlInfo.resource.resourceId + '.' + panelName + '.loadSubPanelsURL';
          this.lmUrlInfoBuilderService.compareSpecUrls(this.formUrlInfo, name, (targetPanel.busFormPanel.subPanelSpec as any).loadSubPanelsURL, loadSubPanelsUrl);
        }

        this.loadSubPanels(targetPanel, loadSubPanelsUrl).subscribe((success: boolean) => {
          let newTargetPanel;
          if (this.formState.selectedSubPanelIndex >= 0) {
            newTargetPanel = this.getSelectedSubPanel(targetPanel);
          } else {
            newTargetPanel = this.getFirstSubPanel(targetPanel);
          }
          this.setSelectComponentId(newTargetPanel.id);
          this.changeDetectionRef.detectChanges();
          this.onBeforeSelectCompleted(request, false, newTargetPanel);
          this.processStepChange();
        });
      } else {
        this.setSelectComponentId(request.targetStepId);
        this.onBeforeSelectCompleted(request, false);
      }
      return;
    }

    // NOOP if clicking on the same step as is now focused
    const currentPanel: LandmarkPanel = this.getSelectedPanelSpec();
    if (request.targetStepId === currentPanel.id && currentPanel.loaded && request.isStepping !== 'none') {
      this.onBeforeSelectCompleted(request, false);
      return;
    }

    const isNext: boolean = 'next' === request.isStepping;
    const currentLMFormBase: LMFormPanelBase = this.getComponentByPanelId(currentPanel.id);
    let panelTransaction = <PanelTransaction>AppConstants.FORM_PANEL_TRANSACTION_NONE_TYPE;
    targetPanel = this.getPanelById(request.targetStepId);
    const isCreateMode = this.isFormCreateMode();

    if (currentLMFormBase) {
      if (currentPanel.type === AppConstants.FORM_PANELTYPE_FORMLIST) {
        const listPanelComponent = currentLMFormBase as LMFormPanelListComponent;
        listPanelComponent.commitCellEdit();
      }
      panelTransaction = this.getPanelTransaction(currentPanel, targetPanel, isNext, currentLMFormBase.isDirty());
    }

    this.lmBusyIndicatorService.showBusyIndicator();
    this.runStepValidation(currentPanel, panelTransaction).pipe(first()).subscribe((validated: boolean) => {
      if (validated) {
        this.onStepValidation(currentPanel, request, panelTransaction);
        // New record is saved and breadcrumb is added.
        // Set hasBreadCrumbs to adjust height of soho stepcontrol. Required for v1, v2
        // v3 does not use the same class in soho override.
        if (isCreateMode && !this.isFormCreateMode()) {
          this.hasBreadcrumbs = this.lmContextService.isBreadCrumbsDisplayed();
        }
      } else {
        this.lmFormPanelBaseService.setStepError(this.getSelectComponentId());
        this.panels = this.flattenPanels(this.lmFormPanelBaseService.getPanels());
        this.onBeforeSelectCompleted(request, true);
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
      }
    });
  }

  onFormPanelContentLoading(event) {
    this.lmBusyIndicatorService.showBusyIndicator();
  }

  onFormPanelContentLoaded(event) {
    this.lmBusyIndicatorService.hideBusyIndicator();
  }

  onContentModified(event: any) {
    this.contentModified.emit(event);

    if (this.enforceLinearWhen) {
      this.updateFirstDirtyPanel();
    }
  }

  onFieldEvent(event) {
    this.fieldEvent.emit(event);
  }

  onStepPanelEvent(event) {
    this.stepPanelEvent.emit(event);
  }

  onPanelTransactionEvent(event: LMPanelTransactionEvent) {
    this.refreshStepDataViews(this.getSelectedPanelSpec(), 'commit', true).pipe(first()).subscribe((finished: boolean) => {
    });
  }

  onDrillAroundEvent(event: LMDrillAroundEvent) {
    this.drillAroundEvent.emit(event);
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onSpecialActionToolbarButtonEvent(event: LMSpecialActionToolbarButtonEvent) {
    this.specialActionToolbarButtonEvent.emit(event);
  }

  isModified(): boolean {
    // composite form ( context form and form panels)
    if (this.model.isDirty) {
      return true;
    }

    return this.isRelatedModified();
  }

  isRelatedModified(): boolean {

    const formPanels: LMFormPanelBase[] = this.panelComponents.toArray();
    for (let i = 0, len = formPanels.length; i < len; i++) {
      const formPanel = formPanels[ i ];
      if (!(formPanel instanceof LMFormPanelFormComponent) && formPanel.isDirty()) {
        return true;
      }
    }

    return false;
  }

  runRefresh() {
    this.updateDataView(true);

    let subStepPanelId;
    let selectedFormPanel: LMFormPanelBase = this.getSelectComponent();
    const selectedLandmarkPanel: LandmarkPanel = this.getSelectedPanelSpec();

    // refresh will remove all the sub panels, so we really need to set the sub panel parent
    if (selectedLandmarkPanel.isSubPanel) {
      subStepPanelId = selectedLandmarkPanel.id; // try to set the selectedSubStep panel.  It may not exists after the refresh!
      selectedFormPanel = this.getComponentByPanelId(selectedLandmarkPanel.parentId);
      this.selectedPanelId = selectedLandmarkPanel.parentId;
    }

    this.updateStepPanels(true);

    // LMCLIENT-20748
    if (!selectedLandmarkPanel || !selectedLandmarkPanel.isValid) {
      this.setSelectComponentId(this.panels[ 0 ].id);
      selectedFormPanel = this.getSelectComponent();
      this.gotoPanelId = this.panels[ 0 ].id;
      return;
    }

    if (selectedFormPanel) {
      if (selectedFormPanel.activated) {
        this.refreshFormPanel(selectedFormPanel, subStepPanelId);
      } else {
        this.activateFormPanel(selectedFormPanel);
      }
    }
  }

  private refreshFormPanel(formPanel: LMFormPanelBase, substepPanelId?: string) {
    if (formPanel.needsReload) {
      // --------------------------------------------------------
      // setting needsReload to false will cause the panel
      // to send any new fromDataView to the panels component.
      // --------------------------------------------------------
      formPanel.needsReload = false;
    }

    const selectedPanel: LandmarkPanel = this.getSelectedPanelSpec();
    if (selectedPanel === undefined) {
      // if selectedPanel is undefined need to select the next or previous available panel
      this.selectedPanelId = this.lmFormPanelBaseService.getClosestPanel(this.selectedPanelId);
      if (this.selectedPanelId !== null) {
        setTimeout(() => this.goToPanel(this.selectedPanelId));
      }
      return;
    }

    const panelName = selectedPanel.busFormPanel.panelName;
    const loadSubPanelsUrl = this.lmUrlUtilService.buildSubPanelDataUrl(this.formUrlInfo, panelName);

    if (selectedPanel.hasSubPanels && !this.lmContextService.useNewSpecUrls()) {
      const name = this.formUrlInfo.resource.resourceId + '.' + panelName + '.loadSubPanelsURL';
      this.lmUrlInfoBuilderService.compareSpecUrls(this.formUrlInfo, name, (selectedPanel.busFormPanel.subPanelSpec as any).loadSubPanelsURL, loadSubPanelsUrl);
    }

    if (selectedPanel && selectedPanel.hasSubPanels) {
      this.loadSubPanels(selectedPanel, loadSubPanelsUrl).subscribe(
        (success: boolean) => {
          if (substepPanelId) {
            // try to set the selectedSubStep panel.  It may not exists!
            const selectedSubStepPanel = this.getPanelById(substepPanelId);
            if (selectedSubStepPanel) {
              this.setSelectComponentId(substepPanelId);
              this.gotoPanelId = substepPanelId;
            }
          }
        });
    }
  }

  runSaveAction(): Observable<boolean> {
    if (this.isWizard()) {
      return this.runWizardSaveAction();
    } else {
      return this.runStepSaveAction();
    }
  }

  private activateFormPanel(formPanel: LMFormPanelBase) {
    // DPB Questioning why this is needed - the spec shouldn't change
    // let panel = this.panels[ this.formState.selectedPanelIndex ];
    // if (panel.type === AppConstants.FORM_PANELTYPE_FORM) {
    //   formPanel.layout = panel.layout;
    //   formPanel.formSpec = panel.formSpec;
    // } else if (panel.type === AppConstants.FORM_PANELTYPE_FORMLIST) {
    //   formPanel.listSpec = panel.listSpec;
    // } else if (panel.type === AppConstants.FORM_PANELTYPE_TEMPLATEFORM) {
    //   formPanel.layout = panel.layout;
    //   formPanel.formSpec = panel.formSpec;
    //   formPanel.specFields = panel.formSpec.busFormSpec.fields;
    // }

    formPanel.needsReload = false;
    formPanel.activated = true;  // this should trigger the template to run

    const selectedPanel: LandmarkPanel = this.getSelectedPanelSpec();

    if (selectedPanel && selectedPanel.hasSubPanels) {
      const panelName = selectedPanel.busFormPanel.panelName;
      const loadSubPanelsUrl = this.lmUrlUtilService.buildSubPanelDataUrl(this.formUrlInfo, panelName);

      if (!this.lmContextService.useNewSpecUrls()) {
        const name = this.formUrlInfo.resource.resourceId + '.' + panelName + '.loadSubPanelsURL';
        this.lmUrlInfoBuilderService.compareSpecUrls(this.formUrlInfo, name, (selectedPanel.busFormPanel.subPanelSpec as any).loadSubPanelsURL, loadSubPanelsUrl);
      }
      this.loadSubPanels(selectedPanel, loadSubPanelsUrl).subscribe(
        (success: boolean) => {
        });
    }
  }

  runWizardSaveAction(): Observable<boolean> {

    return new Observable<boolean>(observer => {
      const panel = this.getSelectedPanelSpec();
      const formPanelBase: LMFormPanelBase = this.getComponentByPanelId(panel.id);

      if (panel.type !== AppConstants.FORM_PANELTYPE_FORM && formPanelBase.isDirty()) {
        formPanelBase.save(observer);
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }

  runStepSaveAction(): Observable<boolean> {
    const formPanels: LMFormPanelBase[] = this.panelComponents.toArray();
    const selectedFormPanel: LMFormPanelBase = this.getSelectComponent();

    return new Observable<boolean>(observer => {
      this.runSavePanels(formPanels).subscribe(
        (continueOn: boolean) => {
          observer.next(continueOn);
          observer.complete();
        },
        (err => {
          const errorPanel: LMFormPanelBase = err;
          if (errorPanel) {
            observer.next(false);
            observer.complete();
          }
        }));
    });
  }

  handleFormViewException(transactionState: LMTransactionState) {
    const selectedPanelId = this.getSelectComponentId();
    const actionResult = transactionState.getTransactionResult();
    const panels: Array<string> = this.lmFormPanelBaseService.getPanelIdsForFieldId(actionResult?.fieldId);

    if (panels.length > 0 && selectedPanelId) {
      const panelInErrorId = panels[0];

      if (selectedPanelId !== panelInErrorId) {
        // clear the error that was set on the selected panel!
        const selectedPanel = this.getPanelById(selectedPanelId);
        const selectedDataView = selectedPanel.isSubPanel ? selectedPanel.subPanelDataView : this.fromDataView;
        this.lmFormPanelBaseService.updateStepIcon(selectedPanel, selectedDataView);
        // set the error on the correct panel!
        this.lmFormPanelBaseService.setStepError(panelInErrorId);
        // move selection to error panel
        this.setSelectComponentId(panelInErrorId);
        this.gotoPanelId = panelInErrorId;
      }
    }
  }

  //  needs to be expanded to allow for substeps and formstate
  isSelected = (panel: LandmarkPanel): string => panel && panel.id === this.selectedPanelId ? 'true' : null;

  getSelectedPanelSpec = (): LandmarkPanel => {
    const selectedPanelId: string = this.getSelectComponentId(); // this.sohoStepProcessComponent.getSelectedStepId();
    return this.getPanelById(selectedPanelId);
  };

  // todo rename to getSelectedComponent
  getSelectComponent = (): LMFormPanelBase => {
    const selectPanelId: string = this.getSelectComponentId();   // this.sohoStepProcessComponent.getSelectedStepId();
    return this.getComponentByPanelId(selectPanelId);
  };

  getSelectComponentId = (): string =>

    // temp value until control can either pass the selected id or fire the beforeStepChange on the initial load.
    this.selectedPanelId; // this.sohoStepProcessComponent.getSelectedStepId();

  setSelectComponentId = (selectedId: string): void => {
    this.selectedPanelId = selectedId;
    const selectedPanel = this.getPanelById(selectedId);

    this.panels.forEach(panel => panel.hideLayout = panel.id !== selectedId);

    if (selectedPanel && selectedPanel.isSubPanel) {
      const parentPanel = this.getPanelById(selectedPanel.parentId);
      const parentIndex = this.lmFormPanelBaseService.getPanelIndexById(selectedPanel.parentId);
      let childIndex = 0;
      for (let i = 0; i < parentPanel.subPanels.length; i++) {
        const childPanel = parentPanel.subPanels[ i ];

        if (childPanel.id === selectedId) {
          childIndex = i;
          break;
        }
      }

      this.formState.selectedPanelIndex = parentIndex;
      this.formState.selectedSubPanelIndex = childIndex;

      if (parentIndex > this.lmFormPanelBaseService.furthestPanelIndex) {
        this.formState.furthestPanelIndex = parentIndex;
      }

      if (childIndex > this.lmFormPanelBaseService.furthestSubPanelIndex) {
        this.formState.furthestSubPanelIndex = childIndex;
      }
    } else {
      const selectedIndex = this.lmFormPanelBaseService.getPanelIndexById(selectedId);
      this.formState.selectedPanelIndex = selectedIndex;
      this.formState.selectedSubPanelIndex = -1;
      this.formState.furthestSubPanelIndex = 0;

      if (selectedIndex > this.lmFormPanelBaseService.furthestPanelIndex) {
        this.formState.furthestPanelIndex = selectedIndex;
      }
    }

    this.lmFormPanelBaseService.furthestPanelIndex = this.formState.furthestPanelIndex || 0;
    this.lmFormPanelBaseService.furthestSubPanelIndex = this.formState.furthestSubPanelIndex || 0;
  };

  getPanelIdByIndex = (index: number): string => {
    if (index >= 0 && index < this.panels.length) {
      return this.panels[ index ].id;
    }
    return null;
  };

  getPanelIndex = (panel: LandmarkPanel): number => {
    if (panel) {
      for (let i = 0, len = this.panels.length; i < len; i++) {
        if (panel.id === this.panels[ i ].id) {
          return i;
        }
      }
    }
    return -1;
  };

  trackById(index: number, panel: LandmarkPanel): string {
    return panel.id;
  }

  public getPanelById(panelId: string): LandmarkPanel {
    return this.panels.find(panel => panel.id === panelId);
  }

  private getPanelByIndex(index: number): LandmarkPanel {
    if (index >= 0 && index < this.panels.length) {
      return this.panels[ index ];
    }
    return null;
  }

  private getNextPanel(panel: LandmarkPanel): LandmarkPanel {
    let index: number = this.getPanelIndex(panel);
    const nextPanel = this.getPanelByIndex(++index);

    if (nextPanel == null) {
      return null; // this case happens when lastPanelNavigation feature is used
    }
    if (nextPanel.isValid && nextPanel.isVisible) {
      if (nextPanel.hasSubPanels && nextPanel.subPanels.length > 0) {
        return nextPanel.subPanels[ 0 ];
      } else {
        return nextPanel;
      }

    } else {
      return this.getNextPanel(nextPanel);
    }
  }

  private getPreviousPanel(panel: LandmarkPanel): LandmarkPanel {
    let index: number = this.getPanelIndex(panel);
    const previousPanel = this.getPanelByIndex(--index);

    if (previousPanel.isValid && previousPanel.isVisible) {
      return previousPanel;
    } else {
      return this.getPreviousPanel(previousPanel);
    }
  }

  private hasPreviousPanel(selectedIndex: number): boolean {
    const start = (selectedIndex - 1);
    for (let i = start; i > -1; i--) {
      if (start === 0 && this.panels[ i ].hasSubPanels) continue;
      if (this.panels[ i ].isValid) {
        return true;
      }
    }
    return false;
  }

  private updateFirstDirtyPanel() {
    let foundDirtyPanel = false;
    const len = this.panels.length;

    for (let i = 0; i < len; i++) {
      const panel = this.panels[i];
      const component: LMFormPanelBase = this.getComponentByPanelId(panel.id);

      if (panel.type === 'form' && !panel.isSubPanel) {
        foundDirtyPanel = component ? component.isDirty() : false;
      } else if (component && component.isDirty()) {
        foundDirtyPanel = true;
      }

      if (foundDirtyPanel) {
        this.firstDirtyPanel = i;
        return;
      }
    }

    this.firstDirtyPanel = -1;
  }

  // DPB not sure about using the method below.  Need to have the next button enable to may run a transaction that enables the next step.
  // private hasNextPanel(selectedIndex: number): boolean {
  //   let start = (selectedIndex + 1);
  //   for (let i = start; i < this.panels.length; i++) {
  //     if (!this.panels[ i ].hidden && this.panels[ i ].isValid) {
  //       return true;
  //     }
  //   }
  //   return false;
  // }

  public nextButtonLabel(): string {
    if (this.sohoStepProcessComponent
      && this.formSpec
      && this.formSpec.busFormSpec.showPanelInfo
      && this.formSpec.busFormSpec.showPanelInfo.nextButtonTitle) {
      const index: number = this.getPanelIndex(this.getSelectedPanelSpec());
      if ((index + 1) === this.panels.length) {
        return this.lmFormatUtilService.formatMessageRule(this.formSpec.busFormSpec.showPanelInfo.nextButtonTitle, this.model);
      }
    }
    return Soho.Locale.translate('Next');
  }

  private updateNavigationControls() {
    const selectedPanel = this.getSelectedPanelSpec();
    const index: number = this.getPanelIndex(selectedPanel);
    const onLastPanel = (index + 1) === this.panels.length;

    // previous button
    this.isPreviousEnabled = this.hasPreviousPanel(index);

    // next button
    if (onLastPanel) {
      const showPanelInfo = this.formSpec.busFormSpec.showPanelInfo;
      this.isNextEnabled = showPanelInfo && showPanelInfo.lastPanelNav ? true : false;
      if (showPanelInfo && showPanelInfo.nextButtonTitle) {
        this.nextButtonText = this.lmFormatUtilService.formatMessageRule(this.formSpec.busFormSpec.showPanelInfo.nextButtonTitle, this.model);
      } else {
        this.nextButtonText = Soho.Locale.translate('Next');
      }
    } else {
      this.isNextEnabled = true;
      this.nextButtonText = Soho.Locale.translate('Next');
    }

    if (this.isDataModNotAllowed) {
      this.isNextEnabled = false;
      this.isPreviousEnabled = false;
    }

    this.updateEnforceLinearSteps();
    this.updateFirstDirtyPanel();
  }

  private onStepValidation(panel: LandmarkPanel, request: BeforeSelectStepEvent, panelTransaction: PanelTransaction): void {
    if ('next' === request.isStepping && this.requiresAcknowledgment(panel)) {
      this.runAcknowledgeAction(panel).pipe(first()).subscribe((acknowledged: boolean) => {
        if (acknowledged) {
          if (panel.type !== AppConstants.FORM_PANELTYPE_FORM) {
            // change panelTransaction to 'commit' to force a refresh, the acknowledgeAction may
            // have been used to update state fields!
            panelTransaction = 'commit';
          }
          this.onStepAcknowledged(request, panelTransaction, panel);
        } else {
          this.onBeforeSelectCompleted(request, true);
          this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        }
      });
    } else {
      this.onStepAcknowledged(request, panelTransaction, panel);
    }
  }

  private onStepAcknowledged(request: BeforeSelectStepEvent, panelTransaction: PanelTransaction, panel: LandmarkPanel) {
    this.refreshStepDataViews(panel, panelTransaction, true).pipe(first()).subscribe((finished: boolean) => {
      this.updateStepPanels(false);
      this.onStepsRefreshed(request, panel);
    });
  }

  private onStepsRefreshed(request: BeforeSelectStepEvent, panel: LandmarkPanel) {
    const targetPanel = this.getBeforeSelectTargetPanel(request, panel);

    if (targetPanel == null) {
      this.handleLastPanelNavigation(request);
      return;
    }

    const targetComponent = this.getComponentByPanelId(targetPanel.id);
    const loadSubPanels = targetComponent ? (!targetComponent.activated || targetComponent.needsReload) : (!targetPanel.activated || targetPanel.needsReload);

    if (targetComponent && targetComponent.needsReload) {
      targetComponent.needsReload = false;
    }
    if (targetPanel.needsReload) {
      targetPanel.needsReload = false;
    }

    if (targetPanel.hasSubPanels && loadSubPanels) {
      const panelName = targetPanel.busFormPanel.panelName;
      const loadSubPanelsUrl = this.lmUrlUtilService.buildSubPanelDataUrl(this.formUrlInfo, panelName);

      if (!this.lmContextService.useNewSpecUrls()) {
        const name = this.formUrlInfo.resource.resourceId + '.' + panelName + '.loadSubPanelsURL';
        this.lmUrlInfoBuilderService.compareSpecUrls(this.formUrlInfo, name, (targetPanel.busFormPanel.subPanelSpec as any).loadSubPanelsURL, loadSubPanelsUrl);
      }

      this.loadSubPanels(targetPanel, loadSubPanelsUrl).subscribe((success: boolean) => {
        const newTargetPanel = this.getFirstSubPanel(targetPanel);
        this.setSelectComponentId(newTargetPanel.id);
        this.changeDetectionRef.detectChanges();
        this.onBeforeSelectCompleted(request, false, newTargetPanel);
        this.ngZone.run(args => this.processStepChange());
      });
    } else {
      this.setSelectComponentId(targetPanel.id);
      this.changeDetectionRef.detectChanges();
      this.onBeforeSelectCompleted(request, false, targetPanel);
      this.ngZone.run(args => this.processStepChange());
    }
  }

  private handleLastPanelNavigation(request: BeforeSelectStepEvent): void {
    if (this.isLastPanelNavigation(request)) {
      this.runLastPanelNavigation();
      this.onBeforeSelectCompleted(request, false);
    } else {
      this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
    }
  }

  private refreshFormDataView(panel: LandmarkPanel, callBack: Observer<boolean>) {
    const panelTransactionEvent = new LMPanelTransactionEvent(panel.id);
    panelTransactionEvent.callBack = callBack;
    this.panelTransactionEvent.emit(panelTransactionEvent);
  }

  private onBeforeSelectCompleted(request: BeforeSelectStepEvent, vetoEvent: boolean, targetPanel?: LandmarkPanel) {
    const result: BeforeSelectStepResult = <BeforeSelectStepResult> {};
    result.continue = !vetoEvent;
    if (targetPanel && targetPanel.id !== request.targetStepId) {
      result.overrideTargetStepId = targetPanel.id;
    }

    request.response(result);
  }

  private getFirstSubPanel(targetPanel: LandmarkPanel): LandmarkPanel {
    if (!targetPanel.activated) {
      targetPanel.activated = true;
    } else if (targetPanel.needsReload) {
      targetPanel.needsReload = false;
    }

    let newTargetPanel = targetPanel;

    for (let i = 0, len = targetPanel.subPanels.length; i < len; i++) {
      const subPanel = targetPanel.subPanels[ i ];
      if (subPanel.isValid) {
        newTargetPanel = subPanel;
        break;
      }
    }

    return newTargetPanel;
  }

  private getSelectedSubPanel(targetPanel: LandmarkPanel): LandmarkPanel {
    let newTargetPanel = this.getFirstSubPanel(targetPanel); // default to first sub panel in case selectedSubPanelIndex lost.;

    if (targetPanel.subPanels.length >= this.formState.selectedSubPanelIndex) {
      const subPanel = targetPanel.subPanels[ this.formState.selectedSubPanelIndex ];
      if (subPanel.isValid) {
        newTargetPanel = subPanel;
      }
    }

    return newTargetPanel;
  }

  private getBeforeSelectTargetPanel(request: BeforeSelectStepEvent, currentPanel: LandmarkPanel): LandmarkPanel {
    if ('next' === request.isStepping || 'prev' === request.isStepping) {
      return ('next' === request.isStepping) ? this.getNextPanel(currentPanel) : this.getPreviousPanel(currentPanel);
    } else {
      return this.getPanelById(request.targetStepId);
    }
  }

  private refreshSubStepDataViews(panel: LandmarkPanel, callBack: Observer<boolean>) {
    const headerPanel: LandmarkPanel = this.getPanelById(panel.parentId);
    const transactionUrlTemplate = urltemplate.parse(this.formUrlInfo.transactionUrl);
    const formUrl: string = transactionUrlTemplate.expand({ resourceId: this.model.getData().resourceId });
    const dataViews: LMDataView[] = [];

    headerPanel.subPanels.forEach((subPanel, index) => {
      dataViews.push(subPanel.subPanelDataView);
    });

    this.lmFormRequestService.refreshSubPanelViews(formUrl, dataViews).pipe(first()).subscribe((refreshResult: any) => {
      this.lmLoggerService.log([ this, 'refreshSubStepDataViews Response: ' + JSON.stringify(refreshResult) ]);
      if (refreshResult instanceof Array) {
        headerPanel.subPanels.forEach((subPanel, index) => {
          subPanel.subPanelDataView = refreshResult[ index ];
        });
      }
      this.refreshFormDataView(panel, callBack);
    });
  }

  private waitRefreshSubStepDataView(subStepPanel: LandmarkPanel, dataView: LMDataView):  Observable<LMDataView> {
    return new Observable<LMDataView>(observer => {
      this.refreshSubStepDataView(subStepPanel, dataView, observer);
    });
  }

  private refreshSubStepDataView(subStepPanel: LandmarkPanel, dataView: LMDataView, observer?: Observer<LMDataView>) {
    const transactionUrlTemplate = urltemplate.parse(this.formUrlInfo.transactionUrl);
    const formUrl: string = transactionUrlTemplate.expand({ resourceId: this.model.getData().resourceId });
    const dataViews: LMDataView[] = [ dataView ];

    this.lmFormRequestService.refreshSubPanelViews(formUrl, dataViews).pipe(first()).subscribe((refreshResult: any) => {
      this.lmLoggerService.log([ this, 'refreshSubStepDataViews Response: ' + JSON.stringify(refreshResult) ]);
      if (refreshResult instanceof Array) { // should only be one
        refreshResult.forEach(refreshedDataView => {
          subStepPanel.subPanelDataView = refreshedDataView;
        });
      }

      if (observer) {
        observer.next(subStepPanel.subPanelDataView);
        observer.complete();
      }
    });
  }

  private refreshStepDataViews(panel: LandmarkPanel, panelTransaction: PanelTransaction, refreshForm: boolean): Observable<boolean> {
    return new Observable<boolean>(observer => {
      if ('commit' === panelTransaction && panel && panel.type !== AppConstants.FORM_PANELTYPE_FORM) {
        if (panel.isSubPanel) {
          this.refreshSubStepDataViews(panel, observer);
        } else if (refreshForm) {
          this.refreshFormDataView(panel, observer);
        } else {
          observer.next(true);
          observer.complete();
        }
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }

  private processStepChange() {
    this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

    if (this.formReloading) {
      // the form is doing a post registration and will reload
      // the form state has been updated, but lets not try to load the next step
      // LMWEB-19262 - this.formReloading need not be updated to false. This component will be
      // rebuilt after security automatically logins and redirects to this form.
      return;
    }

    this.updateNavigationControls();
    const panelComponent: LMFormPanelBase = this.getSelectComponent();
    if (panelComponent == null) {
      return;
    }

    if (!panelComponent.activated || panelComponent.needsReload) {
      if (!(panelComponent instanceof LMFormPanelFormComponent) && !(panelComponent instanceof LMFormPanelUrlComponent)) {
        this.lmBusyIndicatorService.showBusyIndicator();
      }

      const selectedLandmarkPanel: LandmarkPanel = this.getSelectedPanelSpec();
      this.runVisitedAction(selectedLandmarkPanel).pipe(first()).subscribe((visited: boolean) => {
        if (selectedLandmarkPanel.isSubPanel) {
          if ('templateform' === selectedLandmarkPanel.type) {
            panelComponent.fromDataView = this.model.getData();
          } else {
            panelComponent.fromDataView = selectedLandmarkPanel.subPanelDataView;
          }
        } else {
          panelComponent.fromDataView = this.model.getData();
        }
        if (!panelComponent.activated) {
          panelComponent.activated = true;
        } else if (panelComponent.needsReload) {
          panelComponent.needsReload = false;
        }
      });
    }
  }

  private loadSubPanels(landmarkPanel: LandmarkPanel, loadSubPanelsURL: string): Observable<boolean> {
    return new Observable<boolean>(observer => {
      this.lmLoggerService.time({ id: 'lmformcontentstepprocess loadSubPanels()' });
      this.lmBusyIndicatorService.showBusyIndicator();
      this.lmFormPagingService.loadSubPanels(this.model.getData(), loadSubPanelsURL)
        .subscribe(
          (response: any) => {
            this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
            this.lmLoggerService.timeEnd('lmformcontentstepprocess loadSubPanels()');
            if (response.status === AppConstants.STATUS_COMPLETED) {
              const subPanelList = this.lmFormPanelBaseService.buildSubStepPanels(landmarkPanel.busFormPanel, landmarkPanel, response);
              const idx = this.getPanelIndex(landmarkPanel);
              landmarkPanel.subPanels = subPanelList;
              this.panels = this.flattenPanels(this.panels);
              observer.next(true);
              observer.complete();
            }
          },
          (error: any) => {
            this.lmLoggerService.timeEnd('lmformcontentstepprocess loadSubPanels()');
            this.lmLoggerService.error([ 'lmformcontentstepprocess loadSubPanels()', error ]);
            this.lmErrorUtilService.handleUnexpectedError(error, this.placeholder);
            observer.next(false);
            observer.complete();
          }
        );
    });
  }

  private updateEnforceLinearSteps() {
    if (this.formSpec.busFormSpec.showPanelInfo && this.formSpec.busFormSpec.showPanelInfo.linearWhen) {
      this.enforceLinearWhen = this.lmConditionUtilService.isValid([ this.formSpec.busFormSpec.showPanelInfo.linearWhen ], this.model);
    }
  }

  public isWizard(): boolean {
    // make this and all the other methods used in the template setters
    return this.formSpec.busFormSpec.isWizard ? this.formSpec.busFormSpec.isWizard : false;
  }

  private getPanelTransaction(currentPanel: LandmarkPanel, targetPanel: LandmarkPanel, isNext: boolean, isPanelDirty: boolean): PanelTransactionEnum {
    const options: LMGetPanelTransactionOptions = {
      busFormSpec: this.formSpec.busFormSpec,
      currentPanel,
      targetPanel,
      isNext,
      isPanelDirty,
      isCreateMode: this.isFormCreateMode(),
      enforceLinearWhen: this.enforceLinearWhen
    };
    return this.lmFormPanelBaseService.getPanelTransaction(options);
  }

  private isFormCreateMode(): boolean {
    const dataView = this.model.getData();
    const actionName = dataView.action;
    const actionSpec = this.lmActionService.getActionByName(this.formSpec.busClassSpec.actions, actionName);

    return (actionSpec && actionSpec.type === AppConstants.ACTION_TYPE_CREATE);
  }

  getCurrentStepTitle(): string {
    const landmarkPanel: LandmarkPanel = this.getSelectedPanelSpec();

    if (!landmarkPanel) {
      return '';
    }

    if (landmarkPanel.isSubPanel) {
      const parentPanel = this.getPanelById(landmarkPanel.parentId);
      return parentPanel.title;
    } else {
      return landmarkPanel.title;
    }
  }

  getCurrentSubPanelTitle(): string {
    const landmarkPanel: LandmarkPanel = this.getSelectedPanelSpec();

    if (!landmarkPanel) {
      return '';
    }

    return landmarkPanel.isSubPanel ? landmarkPanel.title : null;
  }

  private getComponentByPanelId(panelId: string): LMFormPanelBase {
    const panelArray: Array<LMFormPanelBase> = this.panelComponents ? this.panelComponents.toArray() : [];
    return panelArray.find(panel => panel.panelId === panelId);
  }

  private buildPanelTransactionContext(panel: LandmarkPanel, dataView: LMDataView, actionSpec: LMActionSpec, formName?: string): LMPanelTransactionArgs {
    let resourceId = dataView.resourceId;

    if (formName) {
      resourceId = resourceId + '.' + formName;
    }

    const transactionUrl = this.lmUrlUtilService.buildUrl(this.lmUrlUtilService.buildFormTransactionUrlOptions(resourceId));
    const actionRedirectUrl = this.lmUrlUtilService.buildUrl(this.lmUrlUtilService.buildFormActionRedirectUrlOptions(dataView, actionSpec));

    const args: LMPanelTransactionArgs = {
      busClassSpec: this.formSpec.busClassSpec,     // This is the wrong busClassSpec for subPanels. // Akila it is needed to run ResponseRequired. I think it it need just to create a LMFormSpec
      transactionUrl,
      actionRedirectUrl,
      placeholder: this.placeHolder
    };

    return args;
  }

  private requiresAcknowledgment(panel: LandmarkPanel): boolean {
    const dataView = panel.isSubPanel ? panel.subPanelDataView : this.model.getData();
    return this.lmFormPanelBaseService.requiresAcknowledgement(panel, dataView);
  }

  private runAcknowledgeAction(panel: LandmarkPanel): Observable<boolean> {
    return new Observable<boolean>(observer => {
    const actionSpec = panel.busFormPanel.acknowledgeAction;
    const dataView = panel.isSubPanel ? panel.subPanelDataView : this.model.getData();
    const args: LMPanelTransactionArgs = this.buildPanelTransactionContext(panel, dataView, actionSpec);

    this.waitRefreshSubStepDataView(panel, dataView).subscribe(refreshedDataView => {
      this.lmFormTransactionService.getActionInfoForActionSpecRefactor(undefined, actionSpec, refreshedDataView, args.actionRedirectUrl).subscribe({
        next: (actionInfo: LMActionInfo) => {
          // may need to handle error if not a parameter form.
          this.lmFormTransactionService.runPanelAction(actionSpec, refreshedDataView, args, actionInfo).subscribe(
              (transactionState: LMTransactionState) => {
                this.lmLoggerService.log([ this, 'runAcknowledgeAction  {' + actionSpec.name + '}: ' + JSON.stringify(transactionState.getTransactionResult()) ]);
                const result: LMActionResult = transactionState.getTransactionResult();

                // only concerned about failures
                if (transactionState.isError || (result && AppConstants.VIEW_EXCEPTION === result.status)) {
                  this.lmErrorUtilService.handleError(result.message, result.details, result.status);
                }

                observer.next(transactionState.isSuccess());
                observer.complete();
              });
        },
        error: (e) => {
          this.lmErrorUtilService.handleError(e.message, e.details, e.status, e.message);
          observer.next(false);
          observer.complete();
        }
      });
    });
    });
  }

  private runVisitedAction(panel: LandmarkPanel): Observable<boolean> {
    return new Observable<boolean>(observer => {
      if (panel.busFormPanel && panel.busFormPanel.visitAction && panel.stepProgress === 'NONE') {
        const actionSpec: LMActionSpec = panel.busFormPanel.visitAction;
        const dataView = panel.isSubPanel ? panel.subPanelDataView : this.model.getData();
        const formName = panel.isSubPanel ? null : this.formSpec.busFormSpec.name;
        const args: LMPanelTransactionArgs = this.buildPanelTransactionContext(panel, dataView, actionSpec, formName);

        if (actionSpec != null) {
          this.lmFormTransactionService.getActionInfoForActionSpecRefactor(undefined, actionSpec, dataView, args.actionRedirectUrl).subscribe({
            next: (actionInfo: LMActionInfo) => {
              this.lmFormTransactionService.runPanelAction(actionSpec, dataView, args, actionInfo).subscribe(
                  (transactionState: LMTransactionState) => {
                    observer.next(true);
                    this.lmLoggerService.log([ this, 'runVisitedAction:  {' + actionSpec.name + '}:' +  JSON.stringify(transactionState.getTransactionResult()) ]);
                    if (transactionState.isSuccess()) {
                      const visitedActionDataView = transactionState.getTransactionResult().dataView;
                      if (panel.isSubPanel) {
                        this.refreshSubStepDataView(panel, dataView);
                      } else {
                        this.updateModelAfterTransaction(panel, visitedActionDataView, this.model);
                      }

                      this.lmFormPanelBaseService.updateStepIcon(panel, visitedActionDataView);
                    }
                  });
            },
            error: (e) => {
              this.lmErrorUtilService.handleError(e.message, e.details, e.status, e.message);
            }
          });
        } else {
          observer.next(true);
        }
      } else {
        observer.next(true);
      }
    });
  }

  public isPanelValid(panel: LandmarkPanel): boolean {
    if (this.isDataModNotAllowed) {
      return false;
    } else if (this.enforceLinearWhen) {
      const selectedIndex: number = this.getPanelIndex(panel);
      const hasPreviousDirtyPanel = this.firstDirtyPanel > -1 ? this.firstDirtyPanel < selectedIndex : false;
      return panel.isValid && (!hasPreviousDirtyPanel && (panel.linearVisited || panel.stepProgress === 'IN_PROGRESS' || panel.stepProgress === 'COMPLETED'));
    } else {
      return panel.isValid;
    }
  }

  private runStepValidation(panel: LandmarkPanel, panelTransaction: PanelTransaction): Observable<boolean> {
    return new Observable<boolean>(observer => {
      if (panelTransaction === AppConstants.FORM_PANEL_TRANSACTION_NONE_TYPE) {
        observer.next(true);
        observer.complete();
      } else {
        const formPanelBase: LMFormPanelBase = this.getComponentByPanelId(panel.id);
        if (panel.type !== AppConstants.FORM_PANELTYPE_FORM) {
          formPanelBase.save(observer, true);
        } else {
          const panelName = panel.busFormPanel.panelName;
          // going to have the form save so i need to mark the tabs for reload
          // Don't listen for model events, since that is in a different thread
          // and causes timing issues.! LMCLIENT-39385
          this.markTabsForReload(false);
          this.onStepPanelEvent(new LMStepPanelEvent(panelName, panelTransaction, observer));
        }
      }
    });
  }

  saveComponentState(): void {
  }

  clearComponentState(): void {
  }

  handleBlockingErrorField(event: LMInvalidFieldEvent) {
    event.alert.scrollIntoView(false);
  }

  private updateDataView(hardRefresh: boolean, originatingList?: string): void {
    this.m_fromDataView = this.model.getData();
    this.markTabsForReload(hardRefresh, originatingList);
  }

  private markTabsForReload(hardRefresh: boolean, originatingList?: string) {
    const formPanels: LMFormPanelBase[] = this.panelComponents ? this.panelComponents.toArray() : [];
    const selectFormPanel = this.getSelectComponent();
    const selectedPanelId = selectFormPanel ? selectFormPanel.panelId : null;

    for (let i = 0, len = formPanels.length; i < len; i++) {
      const formPanel = formPanels[ i ];

      if (formPanel.panelId !== selectedPanelId) {
        formPanel.needsReload = this.nonActivePanelNeedsReload(formPanel, hardRefresh);
      } else {
        // This will update all the lists in a MultiList panel when a save is done.
        // Can NOT emit a panelTransaction event on a list save because the save
        // starts the composite form and that will refresh the panels when the
        // the model emits a modelChanged event. hardrefresh means the user is paging
        // (next/previous/refresh)
        if (formPanel instanceof LMFormPanelMultiListComponent && !hardRefresh) {
          const refreshRequest: LMRefreshRequest = { originatingSourceId: originatingList };
          formPanel.refresh(refreshRequest).subscribe(value => {});
        } else {
          const selectedPanel = this.getSelectedPanelSpec();

          if (selectedPanel && selectedPanel.isSubPanel) {
            formPanel.fromDataView  = 'templateform' === selectedPanel.type ? this.model.getData() : selectedPanel.subPanelDataView ;
          } else {
            formPanel.fromDataView  = this.model.getData();
          }

          if (hardRefresh) {
            formPanel.refresh().subscribe();
          }
        }
      }
    }
  }


  private nonActivePanelNeedsReload(panel: LMFormPanelBase, hardRefresh: boolean): boolean {
    const isUrlPanel = panel instanceof LMFormPanelUrlComponent;
    if (isUrlPanel) {
        // do not mark needs load for an activated url panel unless
        // it is a hard refresh (refresh or paging) this is because needs reload
        // unloads the template and when the panel is activated it loads the template
        // which causes the iframe to reload and the user may lose what they entered
        // in the url param.
      return hardRefresh;
    } else {
      return hardRefresh ? true : !panel.isDirty();
    }
  }

  private runSavePanels(formPanels: LMFormPanelBase[]): Observable<boolean> {

    this.lmBusyIndicatorService.showBusyIndicator();
    const saveWorker: LMStepPanelSaveWorker = new LMStepPanelSaveWorker(formPanels);
    return saveWorker.start();
  }

  private isLastPanelNavigation(request: BeforeSelectStepEvent): boolean {
    return (this.formSpec.busFormSpec.showPanelInfo
        && this.formSpec.busFormSpec.showPanelInfo.lastPanelNav
        && request.isStepping === 'next'
    );
  }

  private runLastPanelNavigation() {
    const uiLink = this.formSpec.busFormSpec.showPanelInfo.lastPanelNav;
    if (uiLink.type === 'navigation') {
      this.runNavigation(<LMNavigationSpec>uiLink);
    } else if (uiLink.type === 'httpURL') {
      this.runHttpUrl(<LMHttpUrl>uiLink);
    }
  }

  private runNavigation(navigationSpec: LMNavigationSpec) {
    const compiledTemplateNavUrl = urltemplate.parse(this.formUrlInfo.navigationUrl);
    const navigationUrl = compiledTemplateNavUrl.expand({
      resourceId:     this.model.resourceId,
      navigationName: navigationSpec.name
    });
    this.lmNavigationService.runNavigation(navigationUrl, undefined, { showAsPdf: navigationSpec.showAsPdf });
  }

  private runHttpUrl(httpUrl: LMHttpUrl) {
    const url: string = this.lmFormatUtilService.formatMessageRule(httpUrl.messageRule, this.model);

    if (url) {
      const option: string = (httpUrl.externalLink) ? 'external' : '';
      this.lmGlobalUtilService.navigate(url, null, option);
    }
  }

  private updateModelAfterTransaction(panel: LandmarkPanel, actionDataView: LMDataView, model: LMModel) {
    const fields  = this.getStepConditionFields(panel, actionDataView);
    this.model.setFields(fields, { notifyListeners: false }).subscribe();
  }

  private updateDataViewAfterTransaction(panel: LandmarkPanel, actionDataView: LMDataView, targetDataView: LMDataView) {
    const fields  = this.getStepConditionFields(panel, actionDataView);

    const keys: Array<string> = Object.keys(fields);

    for (let index = 0, len = keys.length; index < len; index++) {
      const fieldId = keys[ index ];
      this.updateConditionField(fieldId, fields[fieldId], targetDataView);
    }
  }

  private getStepConditionFields(panel: LandmarkPanel, dataView: LMDataView): LMFields {

    const fields: LMFields = {};

    if (panel.busFormPanel.complete) {
      const completedFieldId = this.getStepConditionFieldId(panel.busFormPanel.complete);
      const completedField = dataView.fields[ completedFieldId ];

      if (completedField) {
        fields[ completedFieldId ] = completedField;
      }
    }

    if (panel.busFormPanel.progress) {
      const progressFieldId = this.getStepConditionFieldId(panel.busFormPanel.progress);
      const progressField = dataView.fields[ progressFieldId ];

      if (progressField) {
        fields[ progressFieldId ] = progressField;
      }
    }

    return fields;
  }

  private getStepConditionFieldId(conditions: Array<LMCondition>): string {

    if (!Array.isArray(conditions)) {
      conditions = [ conditions ];
    }

    return conditions[ 0 ].fieldId;
  }

  private updateConditionField(fieldId: string, field: LMField, dataView: LMDataView) {
    const targetField = dataView.fields[ fieldId ];

    if (targetField) {
      targetField.value = field.value;
    }
  }

  private updateStepPanels(clearSubPanels: boolean) {
    if (!NgZone.isInAngularZone()) {
      this.ngZone.run(args =>  this.updateStepPanels(clearSubPanels));
    }
    this.lmFormPanelBaseService.updateCompositePanels(this.formSpec.busFormSpec, this.model, clearSubPanels);
    this.panels = this.flattenPanels(this.lmFormPanelBaseService.getPanels());
  }

  private flattenPanels(panels: Array<LandmarkPanel>): Array<LandmarkPanel> {
    const flattenedPanels: Array<LandmarkPanel> = new Array<LandmarkPanel>();

    for (let i = 0, len = panels.length; i < len; i++) {
      const panel = panels[i];

      if (panel.isSubPanel) {
        continue;
      }

      flattenedPanels.push(panel);

      if (panel.hasSubPanels) {
        for (const item of panel.subPanels) {
          flattenedPanels.push(item);
        }
      }
    }

    return flattenedPanels;
  }

  private goToPanel(panelId: string) {
    if (this.sohoStepProcessComponent) {
      this.processStepChange();
      // this.sohoStepProcessComponent?.showStepPanel('#' + panelId); // https://github.com/infor-design/enterprise-ng/pull/1020 available in 9.4
      (this.sohoStepProcessComponent as any).stepprocess?.showStepPanel('#' + panelId);
    }
  }

  handlePostRegistration() {
    this.formReloading = true;
  }

  getLabel(panelTitle: string, iconToolTip: string, iconDefaultMessage: string): string {
    return ((iconToolTip ? iconToolTip : iconDefaultMessage || '') + ' ' + panelTitle).trim();
  }
}
